import {
  useEffect,
  useMemo,
  useState
} from 'react'

import { useTranslation } from 'react-i18next'
import {
  useMutation,
  useQuery
} from 'react-query'

import { addToast } from 'components/Toast/Toast'
import { ActionCard } from 'components-ref/molecules/ActionCard/ActionCard'
import LayoutHeader from 'features/LayoutHeader/LayoutHeader'
import { useUpdateSettingsNotification } from 'helpers/settings/settings-notifications/useUpdateSettingsNotification'
import WithHeaderSection from 'layouts/WithHeaderSection/WithHeaderSection'
import {
  getNotificationSettings,
  putNotifications
} from 'services/endpoints/settings/notifications/notificationSettings'
import { useNotificationSettingsStore } from 'store/notificationSettingsCacheStore'

import styles from './EditNotifications.module.scss'

type NotificationChannel =
  | 'push'
  | 'in_app'
  | 'toast'
  | 'sms'
  | 'email'

type NotificationCategory =
  | 'model'
  | 'subscriber'

type GlobalNotificationKey =
  | 'push_notifications_enabled'
  | 'in_app_notifications_enabled'
  | 'toast_notifications_enabled'
  | 'sms_notifications_enabled'
  | 'email_notifications_enabled'

type FlatChannelSettings =
  Record<string, boolean>

type GroupedChannelSettings = {
  model?: FlatChannelSettings
  subscriber?: FlatChannelSettings
}

type ChannelSettings =
  | FlatChannelSettings
  | GroupedChannelSettings
  | null
  | undefined

const CHANNEL_CONFIG: Record<
  string,
  {
    channel: NotificationChannel
    globalKey: GlobalNotificationKey
    titleKey: string
  }
> = {
  '?push-notifications': {
    channel: 'push',
    globalKey: 'push_notifications_enabled',
    titleKey: 'notifications:push'
  },

  '?in-app-notifications': {
    channel: 'in_app',
    globalKey: 'in_app_notifications_enabled',
    titleKey: 'notifications:inApp'
  },

  '?toast-notifications': {
    channel: 'toast',
    globalKey: 'toast_notifications_enabled',
    titleKey: 'notifications:toast'
  },

  '?sms-notifications': {
    channel: 'sms',
    globalKey: 'sms_notifications_enabled',
    titleKey: 'notifications:sms'
  },

  '?email-notifications': {
    channel: 'email',
    globalKey: 'email_notifications_enabled',
    titleKey: 'notifications:email'
  }
}

const NOTIFICATION_LABELS: Record<string, string> = {
  new_comment: 'notifications:newComment',
  new_like: 'notifications:newPostLike',
  new_comment_like: 'notifications:newCommentLike',
  new_purchase_like: 'notifications:newPurchaseLike',
  new_post: 'notifications:newPostCreated',
  new_subscriber: 'notifications:newSubscriber',
  new_tip: 'notifications:newTip',

  new_premium_video_purchase:
    'notifications:newPremiumPurchase',

  new_message_purchase:
    'notifications:newMessagePurchase',

  upcoming_stream_reminders:
    'notifications:upcomingStreamReminders',

  subscription_renewal:
    'notifications:subscriptionRenewal',

  new_renewal:
    'notifications:newRenewal',

  new_referral:
    'notifications:newReferral',

  new_replies:
    'notifications:newReplies',

  expired_subscription:
    'notifications:expiredSubscriptions',

  live_stream_invite:
    'notifications:liveStreamInvite',

  stream_started:
    'notifications:streamStarted',

  incoming_friend_request:
    'notifications:incomingFriendRequest',

  accepted_friend_request:
    'notifications:acceptedFriendRequest',

  new_tags:
    'notifications:newTags',

  new_message:
    'notifications:newMessage',

  monthly_newsletter:
    'monthlyNewsletter',

  show_full_notification_email:
    'showFullTextOfTheMessagesInTheNotificationEmail',

  poll_responses:
    'notifications:pollResponses'
}

const isGroupedChannelSettings = (
  settings: ChannelSettings
): settings is GroupedChannelSettings => {
  if (
    !settings ||
    typeof settings !== 'object'
  ) {
    return false
  }

  return (
    Object.prototype.hasOwnProperty.call(
      settings,
      'model'
    ) ||
    Object.prototype.hasOwnProperty.call(
      settings,
      'subscriber'
    )
  )
}

export const EditNotifications = ({
  type
}: {
  type: string
}) => {
  const { t } = useTranslation()

  const [
    selectedCategory,
    setSelectedCategory
  ] = useState<NotificationCategory>(
    'model'
  )

  const notificationSettings =
    useNotificationSettingsStore(
      state => state.settings
    )

  const setNotificationSettings =
    useNotificationSettingsStore(
      state => state.setSettings
    )

  const setGlobalNotificationSetting =
    useNotificationSettingsStore(
      state => state.setGlobalSetting
    )

  const setChannelSetting =
    useNotificationSettingsStore(
      state => state.setChannelSetting
    )

  const updateNotificationCard =
    useUpdateSettingsNotification()

  const config = CHANNEL_CONFIG[type]

  const shouldFetchNotificationSettings =
    !notificationSettings

  const {
    data: notificationData,
    error: notificationSettingsError
  } = useQuery(
    'globalNotificationSettings',
    getNotificationSettings,
    {
      enabled:
        shouldFetchNotificationSettings,
      refetchOnWindowFocus: false
    }
  )

  useEffect(() => {
    if (notificationData) {
      setNotificationSettings(
        notificationData
      )
    }

    if (notificationSettingsError) {
      console.error(
        notificationSettingsError
      )

      addToast(
        'error',
        t(
          'error:errorSomethingWentWrong'
        )
      )
    }
  }, [
    notificationData,
    notificationSettingsError,
    setNotificationSettings,
    t
  ])

  const updateGlobalSetting =
    useMutation(
      async ({
        key,
        value
      }: {
        key: GlobalNotificationKey
        value: boolean
      }) =>
        await putNotifications({
          [key]: value
        }),

      {
        onError: (
          err,
          variables
        ) => {
          console.error(err)

          setGlobalNotificationSetting(
            variables.key,
            !variables.value
          )

          addToast(
            'error',
            t(
              'error:errorSomethingWentWrong'
            )
          )
        }
      }
    )

  /*
   * Hooks must all execute before
   * any conditional return.
   */
  const rawChannelSettings =
    config && notificationSettings
      ? (
          notificationSettings[
            config.channel
          ] as ChannelSettings
        )
      : null

  const grouped =
    isGroupedChannelSettings(
      rawChannelSettings
    )

  /*
   * Fan:
   *
   * {
   *   new_comment: true,
   *   new_like: true
   * }
   *
   * Creator/model:
   *
   * {
   *   creator: {...},
   *   subscriber: {...}
   * }
   */
  const channelSettings =
    useMemo<FlatChannelSettings>(
      () => {
        if (!rawChannelSettings) {
          return {}
        }

        if (
          isGroupedChannelSettings(
            rawChannelSettings
          )
        ) {
          return (
            rawChannelSettings[
              selectedCategory
            ] ?? {}
          )
        }

        return rawChannelSettings
      },
      [
        rawChannelSettings,
        selectedCategory
      ]
    )

  if (
    !config ||
    !notificationSettings
  ) {
    return null
  }

  const {
    channel,
    globalKey,
    titleKey
  } = config

  const globalSettings =
    notificationSettings
      .notificationSettings

  const headerEnabled =
    Boolean(
      globalSettings?.[globalKey]
    )

  const handleHeaderToggle = () => {
    const value = !headerEnabled

    setGlobalNotificationSetting(
      globalKey,
      value
    )

    updateGlobalSetting.mutate({
      key: globalKey,
      value
    })
  }

  const handleSettingToggle = (
    key: string,
    value: boolean
  ) => {
    const nextValue = !value

    /*
     * Flat/fan structure can continue using
     * the existing Zustand helper.
     */
    if (!grouped) {
      setChannelSetting(
        channel,
        key,
        nextValue
      )
    } else {
      /*
       * Grouped/model structure.
       *
       * Update the nested creator/subscriber
       * value directly in the cached settings.
       */
      const currentChannel =
        notificationSettings[
          channel
        ] as GroupedChannelSettings

      setNotificationSettings({
        ...notificationSettings,

        [channel]: {
          ...currentChannel,

          [selectedCategory]: {
            ...(
              currentChannel?.[
                selectedCategory
              ] ?? {}
            ),

            [key]: nextValue
          }
        }
      })
    }

    /*
     * Backend setting is still:
     *
     * channel + notification column.
     *
     * Creator/subscriber is only how
     * the API/UI categorizes the field.
     */
    updateNotificationCard.mutate({
      note: key,
      val: nextValue,
      type: channel
    })
  }

  return (
    <WithHeaderSection
      customClass={
        styles.settings_notifications
      }
      headerSection={
        <LayoutHeader
          type='switch'
          section={t(
            'Notifications'
          )}
          title={t(titleKey)}
          switchActive={
            headerEnabled
          }
          switchFn={
            handleHeaderToggle
          }
        />
      }
    >
      {grouped && (
        <div
          className={
            styles.notification_categories
          }
        >
          <button
            type='button'
            className={
              selectedCategory === 'model'
                ? styles.notification_category_active
                : styles.notification_category
            }
            onClick={() => {
              setSelectedCategory(
                'model'
              )
            }}
          >
            {t(
              'notifications:Creator'
            )}
          </button>

          <button
            type='button'
            className={
              selectedCategory ===
              'subscriber'
                ? styles.notification_category_active
                : styles.notification_category
            }
            onClick={() => {
              setSelectedCategory(
                'subscriber'
              )
            }}
          >
            {t(
              'notifications:Subscriber'
            )}
          </button>
        </div>
      )}

      {Object.entries(
        channelSettings
      ).map(
        ([key, value]) => (
          <ActionCard
            key={`${selectedCategory}-${key}`}
            inputId={`${selectedCategory}-${key}`}
            disabled={
              !headerEnabled
            }
            text={t(
              NOTIFICATION_LABELS[
                key
              ] ?? key
            )}
            toggleActive={
              Boolean(value)
            }
            toggleFn={() =>
              handleSettingToggle(
                key,
                Boolean(value)
              )
            }
            hasRadio
          />
        )
      )}
    </WithHeaderSection>
  )
}